import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faFileInvoice } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ColumnFilter,
  CustomAction,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  FilterType,
  SelectOption,
  UserPermission
} from '@globaleris/ui.library'
import { Button, Typography } from '@material-ui/core'
import { Delete, Edit } from '@material-ui/icons'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCommodity, EnumTipoAdhesionCFlotante, EnumTipoConceptoFlotante } from '../../../../api'
import { FloatingConceptsDataTableModel } from '../../../../api/Argentia/endpoints/floatingConcepts/model/floatingConcepts'
import { CardEmptyContent } from '../../../../components/shared/cardEmptyContent/cardEmptyContent'
import { enumToSelectOptionArray } from '../../../../helpers'
import {
  getCustomerDomain,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage
} from '../../../../redux'
import { EnumFloatingConceptsTabs } from '../../floatingConcepts.model'

interface FloatingConceptsDataTableProps {
  activeTab: EnumFloatingConceptsTabs
  settlementGroupOptionsList?: SelectOption[]
  isFetchingTable: boolean
  floatingConceptsDataTable: FloatingConceptsDataTableModel[]
  userPermissions: UserPermission[] | undefined
  dataTableFilter: ColumnFilter[] | undefined
  dataTableSearch: string | undefined
  onAddFloatingConceptClick: (formData: FloatingConceptsDataTableModel | undefined) => void
  onAddDisabled?: boolean
  onEditFloatingConceptClick: (row: FloatingConceptsDataTableModel) => void
  onDeleteFloatingConceptClick: (row: FloatingConceptsDataTableModel) => void
  onGenerateInvoiceClick?: (selectedRows: FloatingConceptsDataTableModel[]) => void
  onDeleteSelectedFloatingConceptsClick?: (selectedRows: FloatingConceptsDataTableModel[]) => void
}

export const FloatingConceptsDataTable: React.FC<FloatingConceptsDataTableProps> = ({
  activeTab,
  settlementGroupOptionsList,
  isFetchingTable,
  floatingConceptsDataTable,
  userPermissions,
  dataTableFilter,
  dataTableSearch,
  onAddFloatingConceptClick,
  onAddDisabled,
  onEditFloatingConceptClick,
  onDeleteFloatingConceptClick,
  onGenerateInvoiceClick,
  onDeleteSelectedFloatingConceptsClick
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'floatingConcepts'])
  const history = useHistory()

  // const isFloatingConceptsTab = activeTab === EnumFloatingConceptsTabs.floatingConcepts
  const isBalanceActionsTab = activeTab === EnumFloatingConceptsTabs.balanceActions
  // #region Handlers Starts
  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const getToolbarCustomActions = () => {
    let toolbarCustomActions: CustomAction[] = []
    switch (activeTab) {
      case EnumFloatingConceptsTabs.distribucion:
      case EnumFloatingConceptsTabs.refacturacion:
      case EnumFloatingConceptsTabs.penalizacion:
      case EnumFloatingConceptsTabs.manual:
        toolbarCustomActions = [
          {
            icon: <FontAwesomeIcon size={'sm'} icon={faFileInvoice as IconProp} />,
            tooltip: t('floatingConcepts:dataTable.selectToolbarCustomActions.generateInvoice'),
            onClick: selectedRows => {
              onGenerateInvoiceClick && onGenerateInvoiceClick(selectedRows)
            }
            // userPermission?: UserPermission;
          },
          {
            userPermission: UserPermission.EDIT,
            icon: <Delete fontSize="small" />,
            tooltip: t('floatingConcepts:dataTable.selectToolbarCustomActions.delete'),
            onClick: selectedRows => {
              onDeleteSelectedFloatingConceptsClick &&
                onDeleteSelectedFloatingConceptsClick(selectedRows)
            }
          }
        ]
        break
      default:
        break
    }
    return toolbarCustomActions
  }

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 3,
    filterType: FilterType.ADVANCED,
    selectableRows: !isBalanceActionsTab,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: !onAddDisabled,
    customInsertEditModal: true,
    onRowInsert: !onAddDisabled
      ? async () => {
          onAddFloatingConceptClick && onAddFloatingConceptClick(undefined)
          return true
        }
      : undefined,
    analytics: {
      customer: getCustomerDomain(),
      view: t('floatingConcepts:dataTable.analytics')
    },
    selectToolbarCustomActions: getToolbarCustomActions(),
    rowCustomActions: [
      {
        userPermission: UserPermission.EDIT,
        icon: <Edit fontSize="small" />,
        tooltip: t('floatingConcepts:dataTable.customActions.edit'),
        onClick: onEditFloatingConceptClick
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Delete fontSize="small" />,
        tooltip: t('floatingConcepts:dataTable.customActions.delete'),
        onClick: onDeleteFloatingConceptClick
      }
    ],
    onFilterChange: (filter: ColumnFilter[]) => {
      storeDataTableFilterForCurrentPage(filter)
    },
    onSearchChange: (search: string) => {
      storeDataTableSearchForCurrentPage(search)
    }
  }

  const columns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'subjectDesc',
      label: t('floatingConcepts:dataTable.columns.subject'),
      type: DataType.TEXT,
      display: !isBalanceActionsTab
    },
    {
      name: 'contractCode',
      label: t('floatingConcepts:dataTable.columns.contractCode'),
      type: DataType.TEXT,
      display: !isBalanceActionsTab,
      required: true
    },
    {
      name: 'cupsCode',
      label: t('floatingConcepts:dataTable.columns.cups'),
      type: DataType.TEXT,
      display: !isBalanceActionsTab
    },
    {
      name: 'settlementGroupId',
      label: t('floatingConcepts:dataTable.columns.settlementGroup'),
      type: DataType.SELECT,
      selectOptions: settlementGroupOptionsList ?? [],
      display: isBalanceActionsTab
    },
    {
      name: 'conceptCode',
      label: t('floatingConcepts:dataTable.columns.code'),
      type: DataType.TEXT,
      display: true
    },
    {
      name: 'conceptName',
      label: t('floatingConcepts:dataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'conceptDetail',
      label: t('floatingConcepts:dataTable.columns.conceptDetail'),
      type: DataType.TEXT,
      display: !isBalanceActionsTab
    },
    {
      name: 'startDate',
      label: t('floatingConcepts:dataTable.columns.initDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('floatingConcepts:dataTable.columns.endDate'),
      type: DataType.DATE
    },
    {
      name: 'price',
      label: t('floatingConcepts:dataTable.columns.unitPrice'),
      type: DataType.NUMBER,
      customDecimalScale: 8,
      display: false
    },
    {
      name: 'quantity',
      label: t('floatingConcepts:dataTable.columns.quantity'),
      type: DataType.NUMBER,
      customDecimalScale: 8,
      display: false
    },
    {
      name: 'amount',
      label: t('floatingConcepts:dataTable.columns.amount'),
      type: DataType.NUMBER,
      customDecimalScale: 8
    },
    // {
    //   name: 'floatingConceptType',
    //   label: t('floatingConcepts:dataTable.columns.floatingConceptType'),
    //   type: DataType.SELECT,
    //   selectOptions: enumToSelectOptionArray(
    //     EnumTipoConceptoFlotante,
    //     'enums:EnumTipoConceptoFlotante'
    //   ),
    //   display: !isBalanceActionsTab
    // },
    {
      name: 'adhesionType',
      label: t('floatingConcepts:dataTable.columns.adhesion'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(
        EnumTipoAdhesionCFlotante,
        'enums:EnumTipoAdhesionCFlotante'
      ),
      display: !isBalanceActionsTab
    }
  ]

  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  return (
    <>
      {isFetchingTable ? (
        <DataTableSkeleton
          elevation={3}
          rows={5}
          title={<Typography variant="h1">{t('floatingConcepts:dataTable.title')}</Typography>}
        />
      ) : floatingConceptsDataTable.length === 0 ? (
        <CardEmptyContent
          title={t('floatingConcepts:dataTable.title')}
          caption={t('floatingConcepts:components.floatingConceptsDefaultTab.emptyCard.caption')}
        >
          <Button
            size="small"
            color="primary"
            variant="contained"
            disabled={onAddDisabled}
            onClick={() => {
              onAddFloatingConceptClick && onAddFloatingConceptClick(undefined)
            }}
          >
            {t('floatingConcepts:components.floatingConceptsDefaultTab.emptyCard.buttonLabel')}
          </Button>
        </CardEmptyContent>
      ) : (
        <DataTable
          data={floatingConceptsDataTable}
          title={t('floatingConcepts:dataTable.title')}
          columns={columns}
          options={options}
          userPermissions={userPermissions}
          appliedFilter={dataTableFilter}
          appliedSearch={dataTableSearch}
        />
      )}
    </>
  )
}
